import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { LscSupportIssueCategory, LscSupportIssueSeverity } from '../../types';

export interface ReportIssuePrefill {
  leagueId?: string | null;
  surface?: string;
  category?: LscSupportIssueCategory;
  severity?: LscSupportIssueSeverity;
  title?: string;
  description?: string;
  errorCode?: string;
}

interface ReportIssueContextValue {
  isOpen: boolean;
  prefill: ReportIssuePrefill | null;
  openReportIssue: (prefill?: ReportIssuePrefill) => void;
  closeReportIssue: () => void;
}

const ReportIssueContext = createContext<ReportIssueContextValue | null>(null);

export function useReportIssue(): ReportIssueContextValue {
  const ctx = useContext(ReportIssueContext);
  if (!ctx) {
    throw new Error('useReportIssue requires ReportIssueProvider');
  }
  return ctx;
}

export function useReportIssueOptional(): ReportIssueContextValue | null {
  return useContext(ReportIssueContext);
}

export const ReportIssueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [prefill, setPrefill] = useState<ReportIssuePrefill | null>(null);

  const openReportIssue = useCallback((nextPrefill?: ReportIssuePrefill) => {
    setPrefill(nextPrefill ?? null);
    setIsOpen(true);
  }, []);

  const closeReportIssue = useCallback(() => {
    setIsOpen(false);
    setPrefill(null);
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      prefill,
      openReportIssue,
      closeReportIssue,
    }),
    [isOpen, prefill, openReportIssue, closeReportIssue]
  );

  return <ReportIssueContext.Provider value={value}>{children}</ReportIssueContext.Provider>;
};

export default ReportIssueProvider;
